import { useEffect, useState, useCallback } from 'react'
import { doc, setDoc, deleteField, onSnapshot, collection, serverTimestamp, increment } from 'firebase/firestore'
import { db } from '../../firebase'
import { useAuth } from './AuthContext'

export function profileRef(uid) {
  return doc(db, 'users', uid, 'meta', 'profile')
}
function logRef(uid, dayNumber) {
  return doc(db, 'users', uid, 'logs', String(dayNumber))
}
function inventoryRef(uid) {
  return doc(db, 'users', uid, 'meta', 'inventory')
}
function mealTemplatesRef(uid) {
  return doc(db, 'users', uid, 'meta', 'mealTemplates')
}
export function aiPlanRef(uid) {
  return doc(db, 'users', uid, 'meta', 'aiPlan')
}
function customMenusRef(uid) {
  return doc(db, 'users', uid, 'meta', 'customMenus')
}
// Shared (not user-scoped) reference data, seeded once by
// scripts/migrate-library-to-firestore.js from data/exerciseLibrary.js and
// data/dishes.js. Requires a Firestore rule allowing authenticated reads on
// `library/{docId}` - see that script's header comment.
function libraryRef(name) {
  return doc(db, 'library', name)
}

// Profile holds the start date that day 1 of the 90-day plan is anchored to,
// plus optional free-form settings like a target weight.
export function useProfile() {
  const { user } = useAuth()
  const [profile, setProfile] = useState(undefined)

  useEffect(() => {
    if (!user) return
    return onSnapshot(profileRef(user.uid), (snap) => {
      setProfile(snap.exists() ? snap.data() : null)
    })
  }, [user])

  const updateProfile = useCallback(
    (partial) => setDoc(profileRef(user.uid), partial, { merge: true }),
    [user],
  )
  const setStartDate = useCallback((isoDate) => updateProfile({ startDate: isoDate }), [updateProfile])

  return { profile, loading: profile === undefined, setStartDate, updateProfile }
}

export function dayNumberFromStartDate(startDateISO) {
  if (!startDateISO) return 1
  const start = new Date(startDateISO + 'T00:00:00')
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const diffDays = Math.round((today - start) / 86400000) + 1
  return Math.min(Math.max(diffDays, 1), 90)
}

export function useDayLog(dayNumber) {
  const { user } = useAuth()
  const [log, setLog] = useState(undefined)

  useEffect(() => {
    if (!user || !dayNumber) return
    return onSnapshot(logRef(user.uid, dayNumber), (snap) => {
      setLog(snap.exists() ? snap.data() : {})
    })
  }, [user, dayNumber])

  const updateLog = useCallback(
    (partial) =>
      setDoc(
        logRef(user.uid, dayNumber),
        { ...partial, dayNumber, updatedAt: serverTimestamp() },
        { merge: true },
      ),
    [user, dayNumber],
  )

  return { log, loading: log === undefined, updateLog }
}

// Pantry inventory: { [itemKey]: { qty, unit, lastAdded } }. A missing key
// (or qty <= 0) means "need to buy". Quantities are kept accurate by
// deducting from stock whenever a meal/supplement gets checked off (see
// adjustStock), and topped up whenever you log a purchase (see addStock).
export function useInventory() {
  const { user } = useAuth()
  const [inventory, setInventory] = useState(undefined)

  useEffect(() => {
    if (!user) return
    return onSnapshot(inventoryRef(user.uid), (snap) => {
      setInventory(snap.exists() ? snap.data() : {})
    })
  }, [user])

  // Log a purchase: adds `amount` to current stock and stamps today's date.
  const addStock = useCallback(
    (itemKey, amount, unit) => {
      const today = new Date().toISOString().slice(0, 10)
      return setDoc(
        inventoryRef(user.uid),
        { [itemKey]: { qty: increment(amount), unit, lastAdded: today } },
        { merge: true },
      )
    },
    [user],
  )

  // Internal use: shift quantity by `delta` (negative to consume, positive
  // to undo a consumption) without touching lastAdded.
  const adjustStock = useCallback(
    (itemKey, delta) => setDoc(inventoryRef(user.uid), { [itemKey]: { qty: increment(delta) } }, { merge: true }),
    [user],
  )

  return { inventory: inventory || {}, loading: inventory === undefined, addStock, adjustStock }
}

// Subscribes to the whole logs collection at once, for the calendar grid and
// progress charts (90 days is small enough to keep in memory).
export function useAllLogs() {
  const { user } = useAuth()
  const [logs, setLogs] = useState({})
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!user) return
    const unsub = onSnapshot(collection(db, 'users', user.uid, 'logs'), (snap) => {
      const next = {}
      snap.forEach((d) => {
        next[d.id] = d.data()
      })
      setLogs(next)
      setLoading(false)
    })
    return unsub
  }, [user])

  return { logs, loading }
}

// Reusable meal templates: { [id]: { name, items: [{foodKey, qty}], createdAt } },
// user-level (not tied to any day) so a saved meal can be applied to any
// meal slot on any day.
export function useMealTemplates() {
  const { user } = useAuth()
  const [templates, setTemplates] = useState(undefined)

  useEffect(() => {
    if (!user) return
    return onSnapshot(mealTemplatesRef(user.uid), (snap) => {
      setTemplates(snap.exists() ? snap.data() : {})
    })
  }, [user])

  const saveTemplate = useCallback(
    (name, items) => {
      const id = crypto.randomUUID()
      return setDoc(
        mealTemplatesRef(user.uid),
        { [id]: { name, items, createdAt: new Date().toISOString() } },
        { merge: true },
      )
    },
    [user],
  )

  const deleteTemplate = useCallback(
    (id) => setDoc(mealTemplatesRef(user.uid), { [id]: deleteField() }, { merge: true }),
    [user],
  )

  return { templates: templates || {}, loading: templates === undefined, saveTemplate, deleteTemplate }
}

// A user's AI-generated 90-day plan (see lib/aiPlan.js for how it's built).
// null means this account has no AI plan and should fall back to the app's
// static default program - see data/plan.js's getEffectiveDayPlan().
export function useAiPlan() {
  const { user } = useAuth()
  const [aiPlan, setAiPlan] = useState(undefined)

  useEffect(() => {
    if (!user) return
    return onSnapshot(aiPlanRef(user.uid), (snap) => {
      setAiPlan(snap.exists() ? snap.data() : null)
    })
  }, [user])

  return { aiPlan, loading: aiPlan === undefined }
}

// Full-day custom menus: { [id]: { name, meals: [{key,time,label,items:[{foodKey,qty}]}], createdAt } }.
// Distinct from meal templates (one meal slot) - a custom menu bundles all
// of a day's meal slots so it can be applied to any day in one action, by
// bulk-writing that day's log.customMeals (the same per-meal override
// mechanism the single-meal editor already uses).
export function useCustomMenus() {
  const { user } = useAuth()
  const [menus, setMenus] = useState(undefined)

  useEffect(() => {
    if (!user) return
    return onSnapshot(customMenusRef(user.uid), (snap) => {
      setMenus(snap.exists() ? snap.data() : {})
    })
  }, [user])

  const saveMenu = useCallback(
    (name, meals) => {
      const id = crypto.randomUUID()
      return setDoc(customMenusRef(user.uid), { [id]: { name, meals, createdAt: new Date().toISOString() } }, { merge: true })
    },
    [user],
  )

  const deleteMenu = useCallback(
    (id) => setDoc(customMenusRef(user.uid), { [id]: deleteField() }, { merge: true }),
    [user],
  )

  return { menus: menus || {}, loading: menus === undefined, saveMenu, deleteMenu }
}

// The full exercise catalogue (name/category/equipment/difficulty), loaded
// from Firestore rather than bundled into the app - see libraryRef() above.
export function useExerciseLibrary() {
  const [exercises, setExercises] = useState(undefined)

  useEffect(() => {
    return onSnapshot(
      libraryRef('exercises'),
      (snap) => setExercises(snap.exists() ? snap.data().items || [] : []),
      (err) => {
        // Falls back to an empty list (rather than hanging forever) if the
        // Firestore rule for library/** isn't set up yet, or on any other
        // read failure - see migrate-library-to-firestore.js's header.
        console.error('useExerciseLibrary: failed to load library/exercises, falling back to empty list.', err)
        setExercises([])
      },
    )
  }, [])

  return { exercises: exercises || [], loading: exercises === undefined }
}

// The full prepared-dish nutrition database, loaded from Firestore - see
// libraryRef() above. Consumers merge this into data/foods.js's FOODS table
// via registerDishes() rather than importing dishes.js directly.
// Stored as a single JSON string field (dishesJson), not a nested map, since
// Firestore automatically indexes every field path in a document and a
// nested map with one entry per dish (each with a full recipe) blows past
// its per-document index-entry limit - see migrate-library-to-firestore.js's
// comment on the write side.
export function useDishLibrary() {
  const [dishes, setDishes] = useState(undefined)

  useEffect(() => {
    return onSnapshot(
      libraryRef('dishes'),
      (snap) => {
        if (!snap.exists()) return setDishes({})
        try {
          setDishes(JSON.parse(snap.data().dishesJson || '{}'))
        } catch (err) {
          console.error('useDishLibrary: failed to parse library/dishes JSON, falling back to no dishes (static foods only).', err)
          setDishes({})
        }
      },
      (err) => {
        // Same fallback as useExerciseLibrary above: never leave the app's
        // loading gate (see Health.jsx's AppShell) stuck forever just
        // because this collection's read rule/data isn't set up yet.
        console.error('useDishLibrary: failed to load library/dishes, falling back to no dishes (static foods only).', err)
        setDishes({})
      },
    )
  }, [])

  return { dishes, loading: dishes === undefined }
}

// The shared default 90-day plan (workouts/menus/supplements), loaded from
// Firestore - see libraryRef() above. Consumers apply this via
// data/plan.js's registerPlanLibrary() rather than importing
// workouts.js/menus.js/diet.js directly. Stored as a single JSON string
// field (planJson), same reasoning as useDishLibrary() above. Unlike the
// dish library, a complete correct plan is already bundled as the fallback
// (data/workouts.js, data/menus.js, data/diet.js), so a missing or
// unreadable doc resolves to `null` (meaning "keep the bundled plan"),
// not an empty override.
export function usePlanLibrary() {
  const [planLib, setPlanLib] = useState(undefined)

  useEffect(() => {
    return onSnapshot(
      libraryRef('plan'),
      (snap) => {
        if (!snap.exists()) return setPlanLib(null)
        try {
          setPlanLib(JSON.parse(snap.data().planJson || 'null'))
        } catch (err) {
          console.error('usePlanLibrary: failed to parse library/plan JSON, keeping the bundled default plan.', err)
          setPlanLib(null)
        }
      },
      (err) => {
        console.error('usePlanLibrary: failed to load library/plan, keeping the bundled default plan.', err)
        setPlanLib(null)
      },
    )
  }, [])

  return { planLib, loading: planLib === undefined }
}
